import { assign, createMachine } from 'xstate';
import { billingMachine, billingContext } from '~/machines/billing-selection';
import { enrollmentMachine, enrollmentContext } from '~/machines/enrollment-selection';
import { legalMachine, legalContext } from '~/machines/legal-completion';
import { calendlyMachine, calendlyContext } from '~/machines/schedule-meet';
import { userContext, userMachine } from '~/machines/user-creation';
import { utilityContext, utilityMachine } from '~/machines/utility-selection';
import { LDClient, LDFlags } from '~/providers';
import { fetchBillingExperienceOptions, redirectToDashboard, signUpUser } from '~/services/';
import { BlockedStates, DUAL_UTILITIES } from '~/utils/constants';
import { removeKeys } from '~/utils/for-warbler';
import { identify, sendToSegment } from '~/utils/tracking';
import { SmbFunnelContext, SmbFunnelEvent, SmbFunnelParams, SmbFunnelServices } from './types';

export const getBillingExperienceOptions = async context => {
  const utilityId = context.funnelPacket!.utility.id;

  let { billingExperienceOptions } = await fetchBillingExperienceOptions({
    utilityId,
    enrollmentDataSourceName: 'utility_connect',
  });

  billingExperienceOptions = billingExperienceOptions.filter(option => option !== 'dual');
  if (DUAL_UTILITIES.includes(utilityId)) billingExperienceOptions.push('dual');

  return { billingExperienceOptions };
};

export const smbMachine = (params: SmbFunnelParams) =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as SmbFunnelContext,
        events: {} as SmbFunnelEvent,
        services: {} as SmbFunnelServices,
      },
      context: {
        enrollmentOptions: ['utility_connect', 'partner_provided'],
        enrollmentSelection: 'utility_connect',
        queryParams: {},
      },
      id: params.id,
      initial: params.initialState || 'start',
      states: {
        start: {
          always: [
            { cond: 'isBlockSignupWhenResuming', target: 'csUnavailable' },
            {
              cond: 'isResuming',
              target: 'fetchBillingExperienceOptions',
              actions: ['setResumingData', 'identifyResumingUserForTracking'],
            },
            { target: 'utility' },
          ],
        },
        utility: {
          invoke: {
            id: 'utilityMachine',
            src: 'utilityMachine',
            data: utilityContext(funnelContext => ({
              queryParams: funnelContext.queryParams,
              funnelId: params.id,
              ...params?.utility?.context,
            })),
            onDone: [
              {
                cond: 'isSignupUnavailable',
                target: 'csUnavailable',
                actions: 'setUtilitySelection',
              },
              { target: 'user', actions: 'setUtilitySelection' },
            ],
          },
        },
        user: {
          invoke: {
            id: 'userMachine',
            src: 'userMachine',
            data: userContext(funnelContext => ({
              funnelId: params.id,
              queryParams: funnelContext.queryParams,
              requireBusinessName: true,
              requirePhone: funnelContext.utilitySelection?.state.abbreviation === 'IL',
              utility: funnelContext.utilitySelection!,
              zipCode: funnelContext.zipCode!,
              ...params?.user?.context,
            })),
            onDone: { target: 'fetchBillingExperienceOptions', actions: 'setFunnelPacket' },
          },
          on: {
            CHANGE_UTILITY: { target: 'utility', actions: 'clearUtilitySelection' },
          },
        },
        fetchBillingExperienceOptions: {
          invoke: {
            id: 'fetchBillingExperienceOptions',
            src: 'fetchBillingExperienceOptions',
            onDone: { target: 'billingExperience', actions: 'setBillingExperienceOptions' },
            onError: 'unexpected',
          },
        },
        billingExperience: {
          on: {
            SELECTED_BILLING_OPTION: [
              {
                cond: 'shouldAdvanceToCalendlyFromBillingExperience',
                target: 'calendly',
                actions: 'setBillingSelection',
              },
              {
                cond: 'shouldAdvanceOnResumingWithUcb',
                target: 'legal',
                actions: 'setBillingSelection',
              },
              {
                cond: 'shouldAdvanceOnResuming',
                target: 'billing',
                actions: 'setBillingSelection',
              },
              {
                target: 'enrollment',
                actions: ['setBillingSelection'],
              },
            ],
          },
        },
        enrollment: {
          invoke: {
            id: 'enrollmentMachine',
            src: 'enrollmentMachine',
            data: enrollmentContext(funnelContext => ({
              canSkipUtilityConnect: true,
              funnelPacket: funnelContext.funnelPacket!,
              startAtAccountNumber: LDClient.variation(LDFlags.showUtiltityAccountNumberScreen),
              enrollmentOptions: funnelContext.enrollmentOptions,
              advanceOn: ['verified', 'timed_out'],
              ...params?.enrollment?.context,
            })),
            onDone: [
              {
                cond: 'shouldAdvanceToCalendlyFromEnrollment',
                target: 'calendly',
              },
              { cond: 'isUcb', target: 'legal' },
              { target: 'billing' },
            ],
          },
        },
        billing: {
          invoke: {
            id: 'billingMachine',
            src: 'billingMachine',
            data: billingContext(funnelContext => ({
              allowMicroDeposits: true,
              billingSelection: funnelContext.billingSelection,
              billingOptions: funnelContext.billingExperienceOptions,
              funnelPacket: funnelContext.funnelPacket!,
              ...params?.billing?.context,
            })),
            onDone: [
              { cond: 'shouldAdvanceToCalendly', target: 'calendly', actions: 'setPaymentMethod' },
              { target: 'legal', actions: 'setPaymentMethod' },
            ],
          },
          on: {
            SKIP_BILLING: [
              { cond: 'shouldAdvanceToCalendly', target: 'calendly' },
              { target: 'legal' },
            ],
          },
        },
        calendly: {
          invoke: {
            id: 'calendlyMachine',
            src: 'calendlyMachine',
            data: calendlyContext(funnelContext => {
              return {
                billingSelection: funnelContext.billingSelection,
                funnelPacket: funnelContext.funnelPacket!,
                paymentMethod: funnelContext.paymentMethod,
                utmParams: funnelContext.queryParams.utmParams,
                ...params?.legal?.context,
              };
            }),
            onDone: 'legal',
          },
        },
        legal: {
          invoke: {
            id: 'legalMachine',
            src: 'legalMachine',
            data: legalContext(funnelContext => ({
              billingSelection: funnelContext.billingSelection!,
              funnelPacket: funnelContext.funnelPacket!,
              paymentMethod: funnelContext.paymentMethod,
              ...params?.legal?.context,
            })),
            onDone: 'completed',
          },
        },
        completed: {
          id: 'completed',
          entry: 'trackFunnelCompleted',
          initial: 'assigning',
          states: {
            assigning: {
              id: 'assigning',
              invoke: {
                id: 'signUpUser',
                src: 'signUpUser',
                onDone: 'redirecting',
                onError: '#unexpected',
              },
            },
            redirecting: {
              id: 'redirecting',
              type: 'final',
              onEntry: 'redirectToDashboard',
            },
          },
        },
        unexpected: {
          id: 'unexpected',
        },
        csUnavailable: {
          id: 'csUnavailable',
        },
      },
    },
    {
      actions: {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
        clearUtilitySelection: assign(context => ({
          utilitySelection: undefined,
          queryParams: removeKeys(context.queryParams, ['utilityId', 'zipCode']),
        })),
        identifyResumingUserForTracking: context => {
          const { user, utility } = context.queryParams.resumingUser!.packet;
          identify({
            id: user.id,
            email: user.email,
            utilityId: utility.id,
            funnelTrack: utility.funnelTrack,
            state: utility.state.abbreviation,
          });
        },
        redirectToDashboard: context => {
          redirectToDashboard({
            userId: context.funnelPacket!.user.id,
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            billingExperienceType: context.billingSelection,
          });
        },
        setBillingExperienceOptions: assign((_context, event) => ({
          billingExperienceOptions: event.data.billingExperienceOptions,
        })),
        setBillingSelection: assign((_context, event) => ({
          billingSelection: event.data.billingSelection,
        })),
        setPaymentMethod: assign((_context, event) => ({
          paymentMethod: event.data.selectedPaymentMethod,
        })),
        setFunnelPacket: assign((_context, event) => ({
          funnelPacket: event.data.funnelPacket,
        })),
        setUtilitySelection: assign((_context, event) => ({
          utilitySelection: event.data.utility,
          zipCode: event.data.zipCode,
        })),
        setResumingData: assign(context => ({
          funnelPacket: context.queryParams.resumingUser!.packet,
        })),
        trackFunnelCompleted: context => {
          sendToSegment({
            type: 'funnel_completed',
            funnelPacket: context.funnelPacket!,
            track: 'solar',
          });
        },
        /* eslint-disable @typescript-eslint/no-unused-vars */
      },
      guards: {
        isResuming: context => !!context.queryParams?.resumingUser,
        isUcb: context => context.billingSelection === 'ucb',
        shouldAdvanceOnResuming: context =>
          context.funnelPacket!.utilityCredential.verificationStatus! === 'correct',
        shouldAdvanceOnResumingWithUcb: (context, event) =>
          context.funnelPacket!.utilityCredential.verificationStatus! === 'correct' &&
          event.data.billingSelection === 'ucb',
        shouldAdvanceToCalendlyFromBillingExperience: (context, event) =>
          LDClient.variation(LDFlags.calendlyEnabled) &&
          context.funnelPacket!.utilityCredential.verificationStatus! === 'correct' &&
          event.data.billingSelection === 'ucb',
        shouldAdvanceToCalendlyFromEnrollment: context =>
          LDClient.variation(LDFlags.calendlyEnabled) && context.billingSelection === 'ucb',
        shouldAdvanceToCalendly: () => LDClient.variation(LDFlags.calendlyEnabled),
        isBlockSignupWhenResuming: context => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          return shouldBlockSignups && isResumingUserInBlockedStates(context, params);
        },
        isSignupUnavailable: (_context, event) => {
          const shouldBlockSignups = LDClient.variation(LDFlags.shouldBlockSignups);
          return (
            shouldBlockSignups &&
            BlockedStates(params.id).includes(event?.data?.utility.state.abbreviation!)
          );
        },
      },
      services: {
        billingMachine: billingMachine(),
        enrollmentMachine: enrollmentMachine(),
        legalMachine: legalMachine().withConfig({
          services: {
            // NB: terms are handled outside the funnel, so don't display anything
            // eslint-disable-next-line require-await
            fetchTerms: async () => ({ docuSignContracts: [], vnmSolarProjectContracts: [] }),
          },
        }),
        calendlyMachine: calendlyMachine(),
        userMachine: userMachine(),
        utilityMachine: utilityMachine().withConfig({
          guards: {
            shouldShowFriction: context => context.utility?.funnelTrack !== 'solar',
          },
        }),
        // fetch acb and ucb eligibility from the backend, use hardcoded list for dual eligibility
        fetchBillingExperienceOptions: getBillingExperienceOptions,
        signUpUser: async context => {
          const { plan } = await signUpUser({
            utilityAccountId: context.funnelPacket!.utilityAccount.id,
            planId: context.funnelPacket!.customPlan.id,
            enrollmentDataSourceName:
              context.enrollmentSelection! ||
              context.funnelPacket!.utilityAccount.enrollmentDataSourceName ||
              'none',
            billingExperienceType: context.billingSelection!,
            track: context.funnelPacket!.utility.funnelTrack,
          });
          return { planId: plan.id };
        },
      },
    },
  );

export const isResumingUserInBlockedStates = (context, params) => {
  const resumingUser = context.queryParams?.resumingUser;
  return (
    !!resumingUser &&
    BlockedStates(params.id).includes(resumingUser?.packet?.utility.state.abbreviation)
  );
};
